/**
 * STEP-UP VERIFICATION (client side)
 * ----------------------------------
 * The server answers a sensitive request with 428 + { code: 'STEP_UP_REQUIRED',
 * purpose } when it wants the user to prove it's really them. This module is
 * the bridge between that answer and the modal that collects the code.
 *
 * It is deliberately not a React hook: apiCall() is a plain async function used
 * from services, so the prompt has to be reachable from outside the component
 * tree. apiCall asks for a token here, this module raises an event, the
 * <StepUpGate> mounted at the app root shows the modal, and the promise below
 * settles with whatever the user did.
 */

const STORAGE_PREFIX = 'stepUpToken:';

// A resolver per in-flight prompt, so two requests needing the same purpose
// don't each pop a modal — the second waits on the first.
const pending = new Map();

export const STEP_UP_PURPOSES = {
  PROFILE:       'profile_update',
  ACCESS_RIGHTS: 'access_rights',
  PERMANENT_DELETE: 'permanent_delete',
};

// Kept in one place because apiUtil iterates it when a call doesn't say which
// purpose it needs — a purpose missing from here would never have its token
// attached, so the user would be prompted again on every single request.
export const ALL_PURPOSES = Object.values(STEP_UP_PURPOSES);

/** Tokens live in sessionStorage: gone when the tab closes, never persisted. */
export function getStepUpToken(purpose) {
  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + purpose);
    if (!raw) return null;
    const { token, expiresAt } = JSON.parse(raw);
    if (Date.now() >= expiresAt) {
      sessionStorage.removeItem(STORAGE_PREFIX + purpose);
      return null;
    }
    return token;
  } catch {
    return null;
  }
}

export function setStepUpToken(purpose, token, expiresInMinutes) {
  // Expire the local copy slightly early. If the client believes a token is
  // still good for the second it dies on the server, the retry burns the user's
  // verification on a request that 428s anyway.
  const skewMs = 15_000;
  const expiresAt = Date.now() + expiresInMinutes * 60_000 - skewMs;
  sessionStorage.setItem(STORAGE_PREFIX + purpose, JSON.stringify({ token, expiresAt }));
}

export function clearStepUpToken(purpose) {
  sessionStorage.removeItem(STORAGE_PREFIX + purpose);
}

/**
 * Ask the user to verify. Resolves with a token, or null if they cancelled.
 * @param {string} purpose
 * @returns {Promise<string|null>}
 */
export function promptStepUp(purpose) {
  if (pending.has(purpose)) return pending.get(purpose);

  const promise = new Promise(resolve => {
    const settle = (token) => {
      pending.delete(purpose);
      resolve(token || null);
    };
    // If nothing is listening (the gate isn't mounted), don't hang the caller
    // for ever — resolve null so the original request simply fails visibly.
    const delivered = window.dispatchEvent(
      new CustomEvent('stepup:required', { detail: { purpose, settle }, cancelable: true }),
    );
    if (!window.__stepUpGateMounted) settle(null);
    return delivered;
  });

  pending.set(purpose, promise);
  return promise;
}

/** True when a failed response is the server asking for verification. */
export function isStepUpRequired(status, data) {
  return status === 428 && data?.code === 'STEP_UP_REQUIRED';
}
